import { Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { SharedService } from 'src/app/Services/sharedService';
import { TimesheetDatePreset, TimesheetPresetDateRangeService } from 'src/app/Services/timesheet-preset-date-range.service';

const TIMESHEET_MAT_DATE_FORMATS: MatDateFormats = {
  parse: { ...MAT_NATIVE_DATE_FORMATS.parse },
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: 'dd MMM yyyy',
    monthYearLabel: 'MMM yyyy',
  },
};

@Component({
    selector: 'app-user-options',
    templateUrl: './user-options.component.html',
    styleUrls: ['./user-options.component.css'],
    providers: [
        DatePipe,
        { provide: MAT_DATE_FORMATS, useValue: TIMESHEET_MAT_DATE_FORMATS },
    ],
    standalone: false
})
export class UserOptionsComponent {
  /** View mode: day single-date, user single-employee range, period all-employees range. */
  @Input() timesheetMode: 'day' | 'user' | 'period' = 'day';
  @Input() employeeList: any[] = [];
  @Input() teamList: any[] = [];
  @Input() selectedTeamId: number | null = null;
  @Input() shiftList: any[] = [];
  @Input() selectedShiftId: number | null = null;
  @Input() selectedUser: any;
  @Input() startDate: any;
  @Input() endDate: any;
  @Input() selectDate: any;
  @Input() todayDate: any;
  @Output() toggleChange = new EventEmitter<boolean>();
  @Output() userSelection = new EventEmitter<void>();
  @Output() teamSelection = new EventEmitter<number | null>();
  @Output() shiftSelection = new EventEmitter<number | null>();
  @Output() startDateChange = new EventEmitter<any>();
  @Output() endDateChange = new EventEmitter<any>();
  @Output() selectDateChange = new EventEmitter<any>();
  filteredEmpList: any[];
  minDate: any = new Date().toISOString();
  maxDate: any = new Date().toISOString();

  @ViewChild('picker') picker!: MatDateRangePicker<Date>;
  @ViewChild('dateRangeDropdown') dateRangeDropdown!: ElementRef;
  @ViewChild('dateRangeButton') dateRangeButton!: ElementRef;

  isOpenDateDropdown = false;
  readonly MAX_RANGE_DAYS = 90;
  /** When set (e.g. 31 for Period-wise), caps inclusive range length. */
  @Input() maxRangeDays: number = this.MAX_RANGE_DAYS;
  dateRangeError: string | null = null;
  selectedDateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  /** Base preset list; Period-wise can hide unsupported options. */
  presetFilters: { value: TimesheetDatePreset; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'previousMonth', label: 'Previous Month' },
    { value: 'last90days', label: 'Last 3 Months' },
  ];

  constructor(
    private tokenEncryptionService: TokenEncryptionService,
    private _sharedService: SharedService,
    private _timesheetPresetDateRange: TimesheetPresetDateRangeService,
    public datePipe: DatePipe
  ) { }

  get rangePickerStartAt(): Date | null {
    if (this.startDate) {
      return new Date(this.startDate);
    }
    if (this.endDate) {
      return new Date(this.endDate);
    }
    return null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['employeeList']) {
      this.filteredEmpList = this.employeeList || [];
    }
    if (changes['startDate'] || changes['endDate']) {
      this.syncSelectedDateRangeFromInputs();
    }
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.setTimeRangeForDateInputs();
    this.syncSelectedDateRangeFromInputs();
  }

  toggleUserWise(event: any) {
    this.timesheetMode = event.checked ? 'user' : 'day';
    this.emitToggleChange();
  }

  setDayWise() {
    if (this.timesheetMode !== 'day') {
      this.timesheetMode = 'day';
      this.emitToggleChange();
    }
  }

  setUserWise() {
    if (this.timesheetMode !== 'user') {
      this.timesheetMode = 'user';
      this.emitToggleChange();
    }
  }

  emitToggleChange() {
    this.toggleChange.emit(this.timesheetMode === 'user' || this.timesheetMode === 'period');
  }

  private get effectiveMaxRange(): number {
    const n = Number(this.maxRangeDays);
    if (!Number.isFinite(n) || n < 1) {
      return this.MAX_RANGE_DAYS;
    }
    return Math.min(n, this.MAX_RANGE_DAYS);
  }

  userSelectionChange() {
    let employee;
    if (this.employeeList) {
      this.employeeList.forEach((element) => {
        if (element.id === this.selectedUser) {
          employee = element
        }
      });
    }
    this.userSelection.emit(employee);
  }

  // Handle employee selection from app-employee-search component
  onEmployeeSelected(employeeId: string | number) {
    this.selectedUser = employeeId;
    const employee = this.employeeList?.find((emp) => emp.id === employeeId);
    this.userSelection.emit(employee);
  }

  onShiftSelected(value: any) {
    const parsed = value === null || value === undefined || value === '' ? null : Number(value);
    this.selectedShiftId = Number.isFinite(parsed as number) ? (parsed as number) : null;
    this.shiftSelection.emit(this.selectedShiftId);
  }

  onTeamSelected(value: any) {
    const parsed = value === null || value === undefined || value === '' ? null : Number(value);
    this.selectedTeamId = Number.isFinite(parsed as number) ? (parsed as number) : null;
    this.teamSelection.emit(this.selectedTeamId);
  }

  // 🔹 FIX: Add `searchEmployee`
  searchEmployee(val: string) {
    let svalue = val.toLowerCase();
    this.filteredEmpList = this.employeeList.filter((ele) => {
      return ele.name.toLowerCase().includes(svalue);
    });
    if (val == "") {
      this.filteredEmpList = this.employeeList;
    }
    // this.employeeList = this.employeeList.filter(emp => emp.name.toLowerCase().includes(query.toLowerCase()));
  }

  employeeSelected(employee: any) {
    this.userSelection.emit(employee);
  }

  isPreviousDateDisabled(): boolean {
    if (!this.selectDate) {
      return false;
    }
    const newDate = new Date(this.selectDate);
    newDate.setDate(newDate.getDate() - 1);
    return newDate < new Date(this.minDate);
  }

  isNextDateDisabled(): boolean {
    if (!this.selectDate) {
      return false;
    }
    const newDate = new Date(this.selectDate);
    newDate.setDate(newDate.getDate() + 1);
    return newDate > new Date(this.maxDate);
  }

  changeDate(isPrevious: boolean) {
    const dateChange = isPrevious ? -1 : 1;
    const newDate = new Date(this.selectDate);
    newDate.setDate(newDate.getDate() + dateChange);
    
    // Check if new date is within the allowed range (minDate to maxDate)
    const minDateObj = new Date(this.minDate);
    const maxDateObj = new Date(this.maxDate);
    
    if (newDate < minDateObj || newDate > maxDateObj) {
      return; // Don't emit if date is out of 90-day range
    }

    const formattedDate = this.formatDateAsLocalYmd(newDate);
    this.selectDateChange.emit(formattedDate);
  }

  private formatDateAsLocalYmd(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onStartDateChange(event: any) {
    if (event.value) {
      this.startDateChange.emit(event.value);
    }
  }

  onEndDateChange(event: any) {
    if (event.value) {
      this.endDateChange.emit(event.value);
    }
  }

  onSelectDateChange(event: any) {
    this.selectDateChange.emit(event.value);
  }

  openDateDropdown() {
    if (!(this.timesheetMode === 'user' || this.timesheetMode === 'period')) return;
    this.isOpenDateDropdown = !this.isOpenDateDropdown;
    if (this.isOpenDateDropdown) {
      this.dateRangeError = null;
      this.syncSelectedDateRangeFromInputs();
    }
  }

  setDateRangePreset(filter: { value: TimesheetDatePreset }) {
    const min = new Date(this.minDate);
    const max = new Date(this.maxDate);
    const { start, end } = this._timesheetPresetDateRange.getPresetRange(filter.value, max, min);
    const normalized = this.normalizeAndClampRange(start, end, min, max);
    this.dateRangeError = normalized.message;
    this.selectedDateRange.patchValue({ start: normalized.start, end: normalized.end }, { emitEvent: false });
    this.startDateChange.emit(normalized.start);
    this.endDateChange.emit(normalized.end);
    this.isOpenDateDropdown = false;
  }

  setCustomDateRange() {
    this.picker?.open();
  }

  onDropdownStartChanged(event: any) {
    if (event?.value) {
      this.dateRangeError = null;
      this.selectedDateRange.patchValue({ start: event.value }, { emitEvent: false });
    }
  }

  onDropdownEndChanged(event: any) {
    if (event?.value) {
      this.dateRangeError = null;
      this.selectedDateRange.patchValue({ end: event.value }, { emitEvent: false });
      const start = this.selectedDateRange?.value?.start || null;
      const end = this.selectedDateRange?.value?.end || null;
      if (start && end) {
        const min = new Date(this.minDate);
        const max = new Date(this.maxDate);
        const normalized = this.normalizeAndClampRange(start, end, min, max);
        this.dateRangeError = normalized.message;
        this.selectedDateRange.patchValue({ start: normalized.start, end: normalized.end }, { emitEvent: false });
        this.startDateChange.emit(normalized.start);
        this.endDateChange.emit(normalized.end);
        this.isOpenDateDropdown = false;
      }
    }
  }

  get selectedFilterLabel(): string {
    const start = this.selectedDateRange?.value?.start || null;
    const end = this.selectedDateRange?.value?.end || null;
    if (start && end) {
      const s = this.datePipe.transform(start, 'dd MMM yyyy');
      const e = this.datePipe.transform(end, 'dd MMM yyyy');
      return s && e ? `${s} - ${e}` : 'Select Range';
    }
    return 'Select Range';
  }

  get visiblePresetFilters(): { value: TimesheetDatePreset; label: string }[] {
    if (this.timesheetMode === 'period') {
      return this.presetFilters.filter((f) => f.value !== 'today' && f.value !== 'last90days');
    }
    return this.presetFilters;
  }

  private syncSelectedDateRangeFromInputs() {
    const start = this.startDate ? new Date(this.startDate) : null;
    const end = this.endDate ? new Date(this.endDate) : null;
    this.selectedDateRange.patchValue({ start, end }, { emitEvent: false });
  }

  private addDays(d: Date, days: number): Date {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
  }

  /**
   * Inclusive days diff in local time while minimizing DST issues by comparing date-only parts.
   * If end is before start, returns 0.
   */
  private diffInDaysInclusive(start: Date, end: Date): number {
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const ms = e.getTime() - s.getTime();
    if (ms < 0) return 0;
    return Math.floor(ms / 86400000) + 1;
  }

  /**
   * Normalizes a range by clamping to [min, max] and enforcing MAX_RANGE_DAYS.
   * Returns the final range and an optional UX message if any adjustment occurred.
   */
  private normalizeAndClampRange(
    start: Date,
    end: Date,
    min: Date,
    max: Date
  ): { start: Date; end: Date; message: string | null } {
    let s = new Date(start);
    let e = new Date(end);
    let changed = false;
    let message: string | null = null;

    // Clamp to min/max first.
    if (s < min) { s = new Date(min); changed = true; }
    if (e > max) { e = new Date(max); changed = true; }
    if (e < min) { e = new Date(min); changed = true; }
    if (s > max) { s = new Date(max); changed = true; }

    // Ensure start <= end.
    if (e < s) {
      e = new Date(s);
      changed = true;
    }

    // Enforce MAX_RANGE_DAYS by shifting start forward, or end backward if needed.
    const days = this.diffInDaysInclusive(s, e);
    const cap = this.effectiveMaxRange;
    if (days > cap) {
      const desiredStart = this.addDays(e, -(cap - 1));
      s = desiredStart;
      changed = true;
      message = `Date range cannot exceed ${cap} days. We adjusted the start date.`;

      // If shifting start violates min, anchor to min and clamp end.
      if (s < min) {
        s = new Date(min);
        e = this.addDays(s, cap - 1);
        if (e > max) {
          e = new Date(max);
          s = this.addDays(e, -(cap - 1));
          if (s < min) {
            s = new Date(min);
          }
        }
        message = `Date range cannot exceed ${cap} days within the allowed window. We adjusted the range.`;
      }
    }

    if (!message && changed) {
      message = 'Date range adjusted to fit the allowed window.';
    }

    return { start: s, end: e, message };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isOpenDateDropdown) return;
    const target = event.target as HTMLElement;
    const dropdownEl = this.dateRangeDropdown?.nativeElement as HTMLElement | undefined;
    const btnEl = this.dateRangeButton?.nativeElement as HTMLElement | undefined;

    // If click is inside Angular Material overlay (calendar), don't close.
    if (target.closest('.cdk-overlay-container')) {
      return;
    }
    if (dropdownEl && dropdownEl.contains(target)) return;
    if (btnEl && btnEl.contains(target)) return;
    this.isOpenDateDropdown = false;
  }

  // Profile image methods
  getProfileImageUrl(emp: any): string | null {
    if (!emp) return null;

    // Check for profile_image_url in meta first (from API response)
    if (emp.meta && emp.meta.profile_image_url) {
      return emp.meta.profile_image_url;
    }

    // Check for profile_image in meta (alternative custom field)
    if (emp.meta && emp.meta.profile_image) {
      return emp.meta.profile_image;
    }

    // Fallback to avatar_urls (WordPress default)
    if (emp.avatar_urls && emp.avatar_urls[96]) {
      return emp.avatar_urls[96];
    }

    return null;
  }

  getDisplayImageUrl(emp: any): string {
    // Return profile image if available, otherwise return default placeholder
    return this.getProfileImageUrl(emp) || this.getDefaultImageUrl();
  }

  getDefaultImageUrl(): string {
    // Return a data URI for a simple default placeholder icon
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='%23B3B3B3'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
  }

  onImageError(event: any, emp: any): void {
    // Hide the image and show initials if available
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';

    // Find the initials element in the same wrapper (works for both dropdown and trigger)
    const wrapper = imgElement.parentElement;
    if (wrapper) {
      const initialsElement = wrapper.querySelector('.user-options-profile-initials, .user-options-trigger-initials') as HTMLElement;
      if (initialsElement) {
        initialsElement.style.display = 'flex';
      }
    }
  }

  getInitials(emp: any): string {
    if (!emp || !emp.name) return 'U';

    const nameParts = emp.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    return 'U';
  }

  getSelectedEmployee(): any {
    if (!this.selectedUser) return null;
    return this.employeeList.find((emp: any) => emp.id === this.selectedUser);
  }


  setTimeRangeForDateInputs() {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const loggedInUser = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = loggedInUser?.company_id ?? user?.company_id;
    const dateToUse = user?.meta?.today_date || new Date();

    this._sharedService.getValidationDateRangeByCompany(dateToUse, companyId).subscribe((range) => {
      this.maxDate = range.maxDate;
      this.minDate = range.minDate;
    });
  }
}
